import { ref, watch, type Ref } from 'vue';

/**
 * Composable для ленивой загрузки элементов с использованием Intersection Observer
 * @param elementRef - ссылка на DOM элемент
 * @param options - опции для Intersection Observer
 * @returns объект с isVisible - флаг видимости элемента, и методами для вызова в хуках жизненного цикла
 */
export function useLazyLoad(
  elementRef: Ref<HTMLElement | null>,
  options: IntersectionObserverInit = {}
) {
  const isVisible = ref(false);
  let observer: IntersectionObserver | null = null;

  const defaultOptions: IntersectionObserverInit = {
    root: null,
    rootMargin: '200px', // Начинаем загрузку за 200px до появления элемента (когда элемент только приближается)
    threshold: 0.01,
    ...options,
  };

  function setupObserver() {
    if (!elementRef.value) return;

    // Если Intersection Observer не поддерживается, загружаем сразу
    if (!('IntersectionObserver' in window)) {
      isVisible.value = true;
      return;
    }

    // Если уже наблюдаем, не создаем новый observer
    if (observer) return;

    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isVisible.value = true;
          // Отключаем наблюдение после того, как элемент стал видимым
          if (observer && elementRef.value) {
            observer.unobserve(elementRef.value);
            observer.disconnect();
            observer = null;
          }
        }
      });
    }, defaultOptions);

    observer.observe(elementRef.value);
  }

  function cleanupObserver() {
    if (observer && elementRef.value) {
      observer.unobserve(elementRef.value);
      observer.disconnect();
    }
    observer = null;
  }

  // Наблюдаем за изменением elementRef и настраиваем observer когда элемент становится доступен
  watch(
    () => elementRef.value,
    (newValue) => {
      if (newValue && !isVisible.value) {
        setupObserver();
      }
    },
    { immediate: true }
  );

  return {
    isVisible,
    raiseOnMounted: setupObserver,
    raiseOnUnmounted: cleanupObserver,
  };
}

